/* WebComponentBillboard component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { h, Component, Element, Event, EventEmitter } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import BillboardTemplate from './cwc-billboard.template';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import '@cewe-designsystem/webcomponent_scrollable';

/**
 * @slot scroll-left-icon - HTML element used as caption for the button to scroll the billboard to the left e.g. an img or svg
 * @slot scroll-right-icon - HTML element used as caption for the button to scroll the billboard to the right e.g. an img or svg
 */
@Component({
  tag: 'cwc-billboard',
  styleUrl: './cwc-billboard.scss',
  shadow: true,
})
export class BillboardComponent {
  /**
   * element
   */
  @Element()
  element: HTMLElement;

  /**
   * Scrollable picture
   */
  scrollablePictures: HTMLElement;

  /**
   * Event thrown if the billboard is scrolled prev
   *
   * @docs
   * @event on:billboard:prev
   */
  @Event({
    eventName: 'on:billboard:prev',
  })
  onBillboardPrev: EventEmitter;

  /**
   * Event thrown if the billboard scrolled to next
   *
   * @docs
   * @event on:billboard:next
   */
  @Event({
    eventName: 'on:billboard:next',
  })
  onBillboardNext: EventEmitter;

  /**
   * onButtonClick
   * @param direction
   */
  async onButtonClick(direction?: string) {
    if (direction == 'l') {
      // @ts-expect-error migrated from ts-ignore
      await this.scrollablePictures.scrollToPrev();
      // @ts-expect-error migrated from ts-ignore
      const emitData = await this.scrollablePictures.getEmitData();
      this.onBillboardPrev.emit(emitData);
    } else {
      // @ts-expect-error migrated from ts-ignore
      await this.scrollablePictures.scrollToNext();
      // @ts-expect-error migrated from ts-ignore
      const emitData = await this.scrollablePictures.getEmitData();
      this.onBillboardNext.emit(emitData);
    }
  }

  /**
   * handleScrollButtons
   * @param data
   */
  handleScrollButtons(data: Event) {
    this.element.shadowRoot.querySelectorAll('ul > li').forEach((element: Element) => {
      element.removeAttribute('hidden');
    });

    // deactivate left arrow if we hit left scroll end
    // @ts-expect-error migrated from ts-ignore
    if (Math.floor(data.detail.content.left) >= data.detail.viewport.left) {
      this.element.shadowRoot.querySelector('ul > li:first-of-type').setAttribute('hidden', '');
    }

    // deactivate right arrow if we hit right scroll end
    // @ts-expect-error migrated from ts-ignore
    if (Math.floor(data.detail.content.right) <= data.detail.viewport.right) {
      this.element.shadowRoot.querySelector('ul > li:last-of-type').setAttribute('hidden', '');
    }
  }

  /**
   * handleScrollButtonsVisibility
   * @param data
   */
  handleScrollButtonsVisibility(data: Event) {
    // @ts-expect-error migrated from ts-ignore
    if (data.detail.content.width <= data.detail.viewport.width) {
      this.element.shadowRoot.querySelectorAll('ul > li').forEach((element: Element) => {
        element.setAttribute('hidden', '');
      });
    } else {
      this.element.shadowRoot.querySelectorAll('ul > li').forEach((element: Element) => {
        element.removeAttribute('hidden');
      });
    }
  }

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It is important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {}

  /**
   * Called every time the component is disconnected from the DOM, i.e., it calls be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {}

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {}

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {
    this.scrollablePictures = this.element.shadowRoot.querySelector('cwc-scrollable');
    this.disableAspectRatioInScrollableContent();

    this.scrollablePictures.addEventListener('on:scrollable:scroll:start', (data) => this.handleScrollButtons(data));
    this.scrollablePictures.addEventListener('on:scrollable:resize', (data) => {
      this.handleScrollButtonsVisibility(data);
      this.handleScrollButtons(data);
      this.disableAspectRatioInScrollableContent();
    });

    this.scrollablePictures.addEventListener('on:scrollable:aspect-ratio:height:resize', (data) => {
      // @ts-expect-error migrated from ts-ignore
      this.element.style.setProperty('--cwc-billboard-height', data.detail.height + 'px');
    });

    this.scrollablePictures.addEventListener('on:scrollable:aspect-ratio:height:reset', () => {
      this.element.style.removeProperty('--cwc-billboard-height');
    });

    // set initial scroll position to zero
    setTimeout(async () => {
      // @ts-expect-error migrated from ts-ignore
      const data = await this.scrollablePictures.getEmitData();
      this.scrollablePictures.dispatchEvent(
        new CustomEvent('on:scrollable:scroll:start', {
          detail: data,
        }),
      );
    }, 500); /* eslint-disable-line no-magic-numbers */
  }

  private disableAspectRatioInScrollableContent(): void {
    // The CSS class "no-aspect-ratio" is known by the cwc-scrollable.
    this.scrollablePictures.shadowRoot.querySelector('cwc-scrollable-content').classList.add('no-aspect-ratio');
  }

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It is never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It is never called during the first render().
   */
  componentDidUpdate() {}

  /**
   * render
   */
  render() {
    return <BillboardTemplate controller={this} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(BillboardComponent.prototype, AbstractComponent.prototype);

export interface BillboardComponent extends AbstractComponent {}
